import axios from 'axios';
import { AdminProduct, ProductUpdateDto, ErrorResponse } from '../types/product';
import { API_BASE_URL } from './api';

/**
 * Сервис для работы с номенклатурой товаров в админке
 */
class AdminProductService {
    private readonly baseUrl = `${API_BASE_URL}/admin/products`;

    /**
     * Получение токена из localStorage
     */
    private getAuthHeader() {
        const token = localStorage.getItem('accessToken') || localStorage.getItem('authToken');
        return token ? { Authorization: `Bearer ${token}` } : {};
    }

    /**
     * Получение списка всех товаров (включая неактивные)
     */
    async getAllProducts(): Promise<AdminProduct[]> {
        try {
            const response = await axios.get<AdminProduct[]>(this.baseUrl, {
                headers: this.getAuthHeader(),
            });
            return response.data;
        } catch (error) {
            console.error('Ошибка при получении списка товаров:', error);
            throw error;
        }
    }

    /**
     * Получение товара по ID
     */
    async getProductById(id: number): Promise<AdminProduct> {
        try {
            const response = await axios.get<AdminProduct>(`${this.baseUrl}/${id}`, {
                headers: this.getAuthHeader(),
            });
            return response.data;
        } catch (error) {
            console.error(`Ошибка при получении товара с ID ${id}:`, error);
            throw error;
        }
    }

    /**
     * Обновление данных товара
     */
    async updateProduct(id: number, data: ProductUpdateDto): Promise<AdminProduct> {
        try {
            const response = await axios.put<AdminProduct>(`${this.baseUrl}/${id}`, data, {
                headers: this.getAuthHeader(),
            });
            return response.data;
        } catch (error) {
            console.error(`Ошибка при обновлении товара с ID ${id}:`, error);
            throw error;
        }
    }

    /**
     * Загрузка основной картинки товара
     */
    async uploadMainImage(id: number, file: File): Promise<AdminProduct> {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post<AdminProduct>(
                `${this.baseUrl}/${id}/main-image`,
                formData,
                {
                    headers: {
                        ...this.getAuthHeader(),
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            return response.data;
        } catch (error: any) {
            if (error.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            console.error(`Ошибка при загрузке основной картинки для товара ${id}:`, error);
            throw error;
        }
    }

    /**
     * Удаление основной картинки товара
     */
    async removeMainImage(id: number): Promise<AdminProduct> {
        try {
            const response = await axios.delete<AdminProduct>(
                `${this.baseUrl}/${id}/main-image`,
                {
                    headers: this.getAuthHeader(),
                }
            );
            return response.data;
        } catch (error: any) {
            if (error.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            console.error(`Ошибка при удалении основной картинки для товара ${id}:`, error);
            throw error;
        }
    }

    /**
     * Добавление дополнительной картинки товара
     */
    async addAdditionalImage(id: number, file: File): Promise<AdminProduct> {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post<AdminProduct>(
                `${this.baseUrl}/${id}/additional-images`,
                formData,
                {
                    headers: {
                        ...this.getAuthHeader(),
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            return response.data;
        } catch (error: any) {
            if (error.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            console.error(`Ошибка при добавлении дополнительной картинки для товара ${id}:`, error);
            throw error;
        }
    }

    /**
     * Удаление дополнительной картинки товара
     */
    async removeAdditionalImage(id: number, imageUrl: string): Promise<AdminProduct> {
        try {
            const response = await axios.delete<AdminProduct>(
                `${this.baseUrl}/${id}/additional-images`,
                {
                    headers: this.getAuthHeader(),
                    params: { imageUrl },
                }
            );
            return response.data;
        } catch (error: any) {
            if (error.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            console.error(`Ошибка при удалении дополнительной картинки для товара ${id}:`, error);
            throw error;
        }
    }

    /**
     * Загрузка сертификата товара
     */
    async uploadCertificate(id: number, file: File): Promise<AdminProduct> {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post<AdminProduct>(
                `${this.baseUrl}/${id}/certificate`,
                formData,
                {
                    headers: {
                        ...this.getAuthHeader(),
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );
            return response.data;
        } catch (error: any) {
            if (error.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            console.error(`Ошибка при загрузке сертификата для товара ${id}:`, error);
            throw error;
        }
    }

    /**
     * Удаление сертификата товара
     */
    async removeCertificate(id: number): Promise<AdminProduct> {
        try {
            const response = await axios.delete<AdminProduct>(
                `${this.baseUrl}/${id}/certificate`,
                {
                    headers: this.getAuthHeader(),
                }
            );
            return response.data;
        } catch (error: any) {
            if (error.response?.data?.error) {
                throw new Error(error.response.data.error);
            }
            console.error(`Ошибка при удалении сертификата для товара ${id}:`, error);
            throw error;
        }
    }
}

export default new AdminProductService();

